// Pure, dependency-free helpers for the admin user actions. Kept import-free
// (no axios/firebase) so they're unit-testable under the Node test runner.

/**
 * Path for the admin reset-link endpoint. The identifier is URL-encoded so
 * emails containing characters like `+` still resolve to
 * `/users/{identifier}/reset-link` instead of altering the route.
 */
export const resetLinkPath = (identifier: string): string =>
  `/api/v1/users/${encodeURIComponent(identifier)}/reset-link`;

export type RosterOpenTransition<T> = {
  /** Whether the open state actually changed this render. */
  changed: boolean;
  /** Links to retain — `null` once the roster is closed. */
  links: T | null;
  /** True when transitioning to closed (also dismiss the reset dialog). */
  closed: boolean;
};

/**
 * Owns the roster's open-state transition. When the roster goes from open to
 * closed, the generated reset/invite links (shareable credentials) are dropped
 * so they can't re-surface on reopen — including when a mounted parent flips
 * `isOpen` directly instead of going through the modal's own close handler.
 *
 * Extracted so the security-critical cleanup can be unit-tested without
 * rendering the component: given a non-null result and open -> closed, it must
 * return `links: null`.
 */
export const rosterOpenTransition = <T>(
  wasOpen: boolean,
  isOpen: boolean,
  links: T | null
): RosterOpenTransition<T> => {
  if (wasOpen === isOpen) return { changed: false, links, closed: false };
  return { changed: true, links: isOpen ? links : null, closed: !isOpen };
};
